import {
  Alert,
  Badge,
  Box,
  Card,
  Center,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Accordion,
  Flex
} from '@mantine/core';
import { IconHistory, IconEye } from '@tabler/icons-react';
import DataTables from 'components/DataTables';
import Dates from 'components/Dates';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useMediaQuery } from '@mantine/hooks';
import { masterPlanService } from 'services/dacum/masterPlan.service';
import { MasterPlanDetailResponse } from 'types/dacum/masterPlan';

interface SharedMasterPlanDetailProps {
  programId?: string;
  jobFamily?: string;
  year?: string;
}

const extractKeyActionLines = (value: string): string[] =>
  value
    ? value
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
    : [];

const SharedMasterPlanDetail = ({ programId, jobFamily, year }: SharedMasterPlanDetailProps) => {
  const [selectedMasterplanIndex, setSelectedMasterplanIndex] = React.useState(0);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const {
    data: responseData,
    isPending,
    error
  }: {
    data?: MasterPlanDetailResponse;
    isPending: boolean;
    error: unknown;
  } = useQuery({
    queryKey: ['sharedMasterPlanDetail', jobFamily, year, programId],
    queryFn: () => {
      if (jobFamily && year) {
        return masterPlanService.getMasterPlanDetail({
          jobFamily,
          year,
          masterplanId: programId
        });
      }
      // Fallback for old URL format - this won't work with new API structure
      return Promise.reject(new Error('Job Family and Year are required'));
    },
    enabled: Boolean((jobFamily && year) || programId)
  });

  // Extract the selected masterplan from the list
  const masterplanList = responseData?.listMasterplan || [];
  const data = masterplanList[selectedMasterplanIndex];
  const showNavigation = masterplanList.length > 1;

  const curriculumColumns = React.useMemo(
    () => [
      {
        heading: 'Business Unit',
        id: 'businessUnitName',
        sortable: true,
        active: true,
        props: (row: any) => row.businessUnitName || row.businessUnitId || '-'
      },
      {
        heading: 'Job Family',
        id: 'jobFamilyName',
        sortable: true,
        active: true,
        props: (row: any) => row.jobFamilyName || row.jobFamilyId || '-'
      },
      {
        heading: 'Job Band',
        id: 'managementLevel',
        sortable: true,
        active: true,
        props: (row: any) => row.managementLevel || '-'
      },
      {
        heading: 'Competency Type',
        id: 'competencyType',
        sortable: true,
        active: true,
        props: (row: any) => row.competencyType || '-'
      },
      {
        heading: 'School Name',
        id: 'schoolName',
        sortable: true,
        active: true,
        props: (row: any) => row.schoolName || '-'
      },
      {
        heading: 'Job Position',
        id: 'jobPositions',
        sortable: true,
        active: true,
        props: (row: any) => {
          const positions = row.jobPositions || [];
          if (Array.isArray(positions) && positions.length > 0) {
            return (
              <Stack gap={2}>
                {positions.map((pos: any, index: number) => (
                  <Text key={index} fz={14}>
                    {index + 1}. {typeof pos === 'object' && pos?.name ? pos.name : pos}
                  </Text>
                ))}
              </Stack>
            );
          }
          return '-';
        }
      },
      {
        heading: 'Competency Name',
        id: 'competencyName',
        sortable: true,
        active: true,
        props: (row: any) => row.competencyName || '-'
      },
      {
        heading: 'Category',
        id: 'category',
        sortable: true,
        active: true,
        props: (row: any) => row.category || '-'
      },
      {
        heading: 'Level',
        id: 'level',
        sortable: true,
        active: true,
        props: (row: any) => row.level?.toString() || '-'
      },
      {
        heading: 'Courses',
        id: 'courses',
        sortable: false,
        active: true,
        props: (row: any) => {
          const courses = row.courses || [];
          if (courses.length === 0) return '-';

          return (
            <Stack gap={2}>
              {courses.map(
                (
                  course: { courseId: string; courseName: string; positions: string[] },
                  index: number
                ) => (
                  <Text key={course.courseId} fz={14}>
                    {index + 1}. {course.courseName}
                    {course.positions && course.positions.length > 0
                      ? ` (${course.positions.join(', ')})`
                      : ''}
                  </Text>
                )
              )}
            </Stack>
          );
        }
      }
    ],
    []
  );

  const versionCurriculumColumns = React.useMemo(
    () => [
      {
        heading: 'Business Unit',
        id: 'businessUnitId',
        sortable: true,
        active: true,
        props: (row: any) => row.businessUnitId || '-'
      },
      {
        heading: 'Job Position',
        id: 'jobPositionId',
        sortable: true,
        active: true,
        props: (row: any) => row.jobPositionId || '-'
      },
      {
        heading: 'Job Band',
        id: 'jobBand',
        sortable: true,
        active: true,
        props: (row: any) => row.jobBand || '-'
      },
      {
        heading: 'Competency Name',
        id: 'competencyName',
        sortable: true,
        active: true,
        props: 'competencyName'
      },
      {
        heading: 'Category',
        id: 'category',
        sortable: true,
        active: true,
        props: 'category'
      },
      {
        heading: 'Level',
        id: 'level',
        sortable: true,
        active: true,
        props: (row: { level: number }) => row.level.toString()
      },
      {
        heading: 'Description',
        id: 'description',
        sortable: false,
        active: true,
        props: (row: any) => row.description || '-'
      }
    ],
    []
  );

  if (isPending) {
    return (
      <Center py={80}>
        <Loader size='md' />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color='red' title='Error Loading Master Plan'>
        {error instanceof Error ? error.message : 'Failed to load master plan details'}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert color='yellow' title='No Data'>
        No master plan data found for this ID.
      </Alert>
    );
  }

  const statusColorMap: { [key: string]: string } = {
    draft: 'gray',
    pending: 'yellow',
    'pending approval': 'yellow',
    approved: 'teal',
    rejected: 'red',
    active: 'blue'
  };

  const statusLabel = data.status || 'Draft';
  const statusColor = statusColorMap[statusLabel.toLowerCase()] || 'gray';

  // eslint-disable-next-line no-unused-vars
  const keyActionLines = extractKeyActionLines(data.keyAction || '');
  const hasVersions = data.versions && data.versions.length > 0;

  const overviewItems = [
    { label: 'Job Family', value: data.jobFamily || '-' },
    { label: 'Job Band', value: data.jobBand || '-' },
    { label: 'Created By', value: (data as any).createdBy || '-' },
    {
      label: 'Created At',
      value: (data as any).createdAt ? (
        <Dates date={(data as any).createdAt} dateFormat='dateNameTime' />
      ) : (
        '-'
      )
    },
    { label: 'Updated By', value: (data as any).updatedBy || '-' },
    {
      label: 'Updated At',
      value: (data as any).updatedAt ? (
        <Dates date={(data as any).updatedAt} dateFormat='dateNameTime' />
      ) : (
        '-'
      )
    }
  ];

  return (
    <Box>
      <Flex gap='xl' direction={{ base: 'column', md: 'row' }}>
        {/* Main Content */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Stack gap='xl'>
            {/* Modern Header */}
            <Paper
              p='xl'
              radius='lg'
              withBorder
              style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              <Stack gap='md'>
                <Group justify='space-between' align='flex-start'>
                  <Group gap='md'>
                    <Box
                      style={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        padding: '12px',
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      <IconEye size={32} color='white' />
                    </Box>
                    <Stack gap={4}>
                      <Text fw={700} fz={28} c='white'>
                        {data.programName}
                      </Text>
                      <Text fz={15} c='white' opacity={0.9}>
                        {data.jobFamily} • {data.jobBand || 'N/A'}
                      </Text>
                    </Stack>
                  </Group>
                  <Group gap='xs'>
                    <Badge color={statusColor} variant='filled' size='lg' radius='md'>
                      {statusLabel}
                    </Badge>
                    <Badge color='gray' variant='white' size='lg' radius='md'>
                      Read-Only
                    </Badge>
                  </Group>
                </Group>
              </Stack>
            </Paper>

            <Card shadow='sm' radius='lg' withBorder>
              <Stack gap='lg'>
                <Text fw={700} fz={18} c='blue'>
                  Overview
                </Text>

                <Box
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '16px'
                  }}
                >
                  {overviewItems.map((item) => (
                    <Paper key={item.label} withBorder radius='md' p='md'>
                      <Text fz={12} fw={600} c='dimmed' tt='uppercase'>
                        {item.label}
                      </Text>
                      <Text fw={600}>{item.value}</Text>
                    </Paper>
                  ))}
                </Box>

                <Box
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                    gap: '16px'
                  }}
                >
                  <Paper withBorder radius='md' p='md'>
                    <Stack gap={6}>
                      <Text fw={600}>Description</Text>
                      <Text fz={14}>{data.description || '-'}</Text>
                    </Stack>
                  </Paper>
                  {/* Key Actions section removed from display but still in data */}
                </Box>
              </Stack>
            </Card>

            <Card shadow='sm' radius='lg' withBorder>
              <Stack gap='md'>
                <Group justify='space-between' align='center'>
                  <Text fw={700} fz={18} c='blue'>
                    Competency List
                  </Text>
                  <Badge color='grape' variant='light' size='lg' radius='md'>
                    {data.curriculumList.length} item{data.curriculumList.length === 1 ? '' : 's'}
                  </Badge>
                </Group>
                <DataTables
                  columns={curriculumColumns}
                  data={data.curriculumList}
                  loading={false}
                  withPagination={false}
                  emptyMessageTitle='No curriculum data'
                  emptyMessageDescription='No curriculum entries are linked to this master plan.'
                />
              </Stack>
            </Card>

            {hasVersions && (
              <Card shadow='sm' radius='lg' withBorder>
                <Stack gap='md'>
                  <Group gap='md' align='center'>
                    <Box
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '8px',
                        padding: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <IconHistory size={20} color='white' />
                    </Box>
                    <Text fw={700} fz={18} c='blue'>
                      Version History
                    </Text>
                    <Badge color='blue' variant='light' size='lg' radius='md'>
                      {data.versions.length} version{data.versions.length === 1 ? '' : 's'}
                    </Badge>
                  </Group>

                  <Accordion variant='separated' radius='md' chevronPosition='left'>
                    {data.versions.map((version) => (
                      <Accordion.Item
                        key={version.versionNumber}
                        value={`version-${version.versionNumber}`}
                        style={{ border: '2px solid #e9ecef', borderRadius: '8px' }}
                      >
                        <Accordion.Control>
                          <Group justify='space-between' wrap='nowrap' pr='md'>
                            <Group gap='md'>
                              <Badge
                                size='xl'
                                radius='md'
                                variant='gradient'
                                gradient={{ from: 'blue', to: 'grape' }}
                              >
                                v{version.versionNumber}
                              </Badge>
                              <Stack gap={2}>
                                <Text fw={600} fz={15}>
                                  {version.programName}
                                </Text>
                                <Text fz={12} c='dimmed'>
                                  <Dates date={version.createdAt} dateFormat='dateNameTime' />
                                </Text>
                              </Stack>
                            </Group>
                            {version.status && (
                              <Badge
                                color={statusColorMap[version.status.toLowerCase()] || 'gray'}
                                variant='filled'
                                size='md'
                                radius='md'
                              >
                                {version.status}
                              </Badge>
                            )}
                          </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Stack gap='lg'>
                            {/* Version Details Grid */}
                            <Box
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                gap: '12px'
                              }}
                            >
                              <Paper
                                withBorder
                                radius='md'
                                p='md'
                                style={{
                                  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                                }}
                              >
                                <Text fz={11} fw={700} c='dimmed' tt='uppercase' mb={4}>
                                  Job Family
                                </Text>
                                <Text fz={14} fw={600}>
                                  {version.jobFamily}
                                </Text>
                              </Paper>
                              <Paper
                                withBorder
                                radius='md'
                                p='md'
                                style={{
                                  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                                }}
                              >
                                <Text fz={11} fw={700} c='dimmed' tt='uppercase' mb={4}>
                                  Job Band
                                </Text>
                                <Text fz={14} fw={600}>
                                  {version.jobBand || 'N/A'}
                                </Text>
                              </Paper>
                              <Paper
                                withBorder
                                radius='md'
                                p='md'
                                style={{
                                  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                                }}
                              >
                                <Text fz={11} fw={700} c='dimmed' tt='uppercase' mb={4}>
                                  Business Unit
                                </Text>
                                <Text fz={14} fw={600}>
                                  {version.businessUnitId || '-'}
                                </Text>
                              </Paper>
                              <Paper
                                withBorder
                                radius='md'
                                p='md'
                                style={{
                                  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                                }}
                              >
                                <Text fz={11} fw={700} c='dimmed' tt='uppercase' mb={4}>
                                  Curriculum Items
                                </Text>
                                <Text fz={14} fw={600}>
                                  {version.curriculum?.length || 0}
                                </Text>
                              </Paper>
                              <Paper
                                withBorder
                                radius='md'
                                p='md'
                                style={{
                                  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                                }}
                              >
                                <Text fz={11} fw={700} c='dimmed' tt='uppercase' mb={4}>
                                  Courses
                                </Text>
                                <Text fz={14} fw={600}>
                                  {version.courses?.length || 0}
                                </Text>
                              </Paper>
                            </Box>

                            {/* Description */}
                            {version.description && (
                              <Paper withBorder radius='md' p='md' bg='blue.0'>
                                <Text fz={12} fw={700} c='blue.7' tt='uppercase' mb={8}>
                                  Description
                                </Text>
                                <Text fz={14} fw={500}>
                                  {version.description}
                                </Text>
                              </Paper>
                            )}

                            {/* Key Actions section removed from version history display */}

                            {/* Courses List */}
                            {version.courses && version.courses.length > 0 && (
                              <Paper withBorder radius='md' p='md' bg='teal.0'>
                                <Text fz={12} fw={700} c='teal.7' tt='uppercase' mb={8}>
                                  Courses ({version.courses.length})
                                </Text>
                                <Stack gap={4}>
                                  {version.courses.map((course: any, courseIndex: number) => (
                                    <Text key={course.courseId || courseIndex} fz={14} fw={500}>
                                      {courseIndex + 1}. {course.courseName || course.name || '-'}
                                    </Text>
                                  ))}
                                </Stack>
                              </Paper>
                            )}

                            {/* Curriculum Table */}
                            {version.curriculum && version.curriculum.length > 0 && (
                              <Box>
                                <Group mb='sm' align='center'>
                                  <Text fw={700} fz={16} c='grape.8'>
                                    Curriculum Details
                                  </Text>
                                  <Badge color='grape' variant='light' radius='md'>
                                    {version.curriculum.length} items
                                  </Badge>
                                </Group>
                                <DataTables
                                  columns={versionCurriculumColumns}
                                  data={version.curriculum}
                                  loading={false}
                                  withPagination={false}
                                  emptyMessageTitle='No curriculum'
                                  emptyMessageDescription='No curriculum entries for this version.'
                                />
                              </Box>
                            )}

                            {/* Approval History */}
                            {version.approvalHistory && version.approvalHistory.length > 0 && (
                              <Paper withBorder radius='md' p='md' bg='yellow.0'>
                                <Text fz={12} fw={700} c='yellow.9' tt='uppercase' mb={8}>
                                  Approval History ({version.approvalHistory.length})
                                </Text>
                                <Stack gap={8}>
                                  {version.approvalHistory.map(
                                    (approval: any, approvalIndex: number) => (
                                      <Box key={approvalIndex}>
                                        <Text fz={13} fw={600}>
                                          {approval.action || 'Action'} by{' '}
                                          {approval.approver || 'Unknown'}
                                        </Text>
                                        {approval.date && (
                                          <Text fz={12} c='dimmed'>
                                            <Dates date={approval.date} dateFormat='dateNameTime' />
                                          </Text>
                                        )}
                                        {approval.comment && (
                                          <Text fz={12} mt={4}>
                                            {approval.comment}
                                          </Text>
                                        )}
                                      </Box>
                                    )
                                  )}
                                </Stack>
                              </Paper>
                            )}
                          </Stack>
                        </Accordion.Panel>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                </Stack>
              </Card>
            )}
          </Stack>
        </Box>

        {/* Navigation Sidebar - Only show if multiple masterplans */}
        {showNavigation && (
          <Box
            style={{
              width: isMobile ? '100%' : '320px',
              flexShrink: 0
            }}
          >
            <Paper
              withBorder
              radius='lg'
              p='lg'
              shadow='sm'
              style={{
                background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)',
                ...(isMobile ? {} : { position: 'sticky', top: '20px' })
              }}
            >
              <Group gap='xs' mb='lg'>
                <IconHistory size={20} style={{ color: '#228be6' }} />
                <Text fw={700} fz={17} c='blue.7'>
                  Available Master Plans
                </Text>
              </Group>
              <Text fz={12} c='dimmed' mb='md'>
                Select a configuration to view details
              </Text>
              <Box
                style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'row' : 'column',
                  gap: '12px',
                  overflowX: isMobile ? 'auto' : 'hidden',
                  overflowY: isMobile ? 'hidden' : 'auto',
                  maxHeight: isMobile ? 'none' : '600px',
                  paddingBottom: isMobile ? '8px' : '0px',
                  paddingRight: isMobile ? '0px' : '4px'
                }}
              >
                {masterplanList.map((masterplan, index) => {
                  const isSelected = selectedMasterplanIndex === index;
                  return (
                    <Paper
                      key={index}
                      p='md'
                      withBorder
                      radius='lg'
                      shadow={isSelected ? 'md' : 'xs'}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#e7f5ff' : '#ffffff',
                        borderColor: isSelected ? '#228be6' : '#dee2e6',
                        borderWidth: isSelected ? '2px' : '1px',
                        transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                        transition: 'all 0.2s ease',
                        minWidth: isMobile ? '240px' : 'unset',
                        flexShrink: isMobile ? 0 : 1
                      }}
                      onClick={() => setSelectedMasterplanIndex(index)}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                          e.currentTarget.style.transform = 'scale(1.01)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = '#ffffff';
                          e.currentTarget.style.transform = 'scale(1)';
                        }
                      }}
                    >
                      <Stack gap={6}>
                        <Group justify='space-between' wrap='nowrap'>
                          <Text fw={700} fz={15} c={isSelected ? 'blue.9' : 'dark.7'} lineClamp={1}>
                            {masterplan.jobBand}
                          </Text>
                          {isSelected && (
                            <IconEye size={18} style={{ color: '#228be6', flexShrink: 0 }} />
                          )}
                        </Group>
                        <Text fz={13} c={isSelected ? 'blue.7' : 'dimmed'} lineClamp={1}>
                          {masterplan.jobFamily}
                        </Text>
                        <Text fz={12} c='dimmed' lineClamp={1}>
                          {masterplan.businessUnitName || masterplan.businessUnitId}
                        </Text>
                        <Group gap='xs' mt={4}>
                          <Badge
                            size='sm'
                            radius='md'
                            variant={isSelected ? 'filled' : 'light'}
                            color={
                              masterplan.status?.toLowerCase() === 'approved'
                                ? 'teal'
                                : masterplan.status?.toLowerCase() === 'rejected'
                                  ? 'red'
                                  : 'yellow'
                            }
                          >
                            {masterplan.status || 'Pending'}
                          </Badge>
                          <Badge size='sm' radius='md' variant='light' color='gray'>
                            {masterplan.curriculumList?.length || 0} items
                          </Badge>
                        </Group>
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>
            </Paper>
          </Box>
        )}
      </Flex>
    </Box>
  );
};

export default SharedMasterPlanDetail;
